// ChannelDevicesPanel.tsx — onglet Chaînes du Dashboard : les smartphones connectés aux
// régies pour un direct (multi-angle)
//
// Ajouté le 2026-10-20. Données : admin-api DashboardLatest (`channelDevices`), relu du
// journal que la régie écrit à chaque connexion d'un smartphone (AdminActionLog,
// module RemoteDeviceConnection). Toutes les chaînes y figurent, même créées avant la
// période : c'est la connexion qui doit tomber dans la période, pas la création.
//
// La position est celle de l'adresse IP (sur un réseau mobile, la ville de
// l'opérateur), pas le GPS du téléphone : la régie le reçoit (onglet Carte) mais ne
// le garde pas. Créateur ou invité : présumé d'après l'IP (même IP publique que la régie
// = le créateur), voir DashboardLatest::byCreator. « Fin inconnue » : aucune déconnexion journalisée (régie fermée avant
// le téléphone) et la connexion date de plus de 12 h.

import React, { useEffect, useRef, useState } from 'react';

import styles from './ChannelsSection.module.scss';
import tabs   from './DashboardTabs.module.scss';

import { Geo, ExtraLayer, Picked } from './ActivityOverview';
import { creatorPlace, dayLabel, hour, ownerText, plural, openChannel, scrollToChannel, spoken } from './dashboardShared';
import { ExternalLink } from 'lucide-react';

export interface DeviceSession {
  name           : string;
  ip             : string | null;
  transport      : string | null;
  /** « Kleon Studio iOS », « Web (iOS) »… : client du téléphone (régie d'après le 2026-10-24 : absent avant). */
  client?        : string | null;
  regieIp?       : string | null;
  /** Lieu du poste de la régie qui a écrit la ligne (admin-api d'après le 2026-10-20). */
  regieGeo?      : (Geo & { isp?: string | null }) | null;
  /** « macOS 10.15.7 · Chrome 155.0.0.0 » : système et navigateur de ce poste (admin-api d'après le 2026-10-23). */
  regieClient?   : string | null;
  /** Même IP que la régie : présumé du créateur ; null si une IP manque (admin-api d'avant le 2026-10-20 : absent). */
  byCreator?     : boolean | null;
  connectedAt    : string;
  disconnectedAt : string | null;
  connected      : boolean;
  geo            : (Geo & { isp?: string | null }) | null;
}

export interface ChannelDevices {
  channelId   : number;
  channelName : string;
  publicRef   : string;
  channelOn   : boolean;
  owner       : string;
  ownerEmail  : string;
  devices     : DeviceSession[];
}

/** Un direct (diffusion) de la période, avec ou sans smartphone (admin-api d'après le 2026-10-27). */
export interface ChannelDirect {
  id          : number;
  channelId   : number;
  channelName : string;
  publicRef   : string;
  deleted     : boolean;
  owner       : string;
  ownerEmail  : string;
  startDate   : string;
  endDate     : string | null;
  /** Programmes passés à l'antenne pendant la diffusion ; la mire est ce qu'il y a entre eux. */
  programs    : { mediaId: number; name: string; start: string; end: string | null }[];
}

/** Sans date de fin depuis plus de 12 h : la fin est inconnue, le direct n'est plus dit « en cours ». */
const directOpen = (x: ChannelDirect) => !x.endDate && Date.now() - new Date(x.startDate).getTime() < 12 * 3600 * 1000;

export const DEVICE_COLOR = '#F472B6';
export const REGIE_COLOR  = '#1E3A8A';

const at = (d: DeviceSession) => new Date(d.connectedAt).getTime();

/** Fin de la connexion : l'heure (et le jour s'il diffère du début), « en cours » ou « inconnue ». */
export const sessionEnd = (d: DeviceSession) =>
  d.disconnectedAt
    ? (dayLabel(d.disconnectedAt) === dayLabel(d.connectedAt) ? hour(d.disconnectedAt) : `${dayLabel(d.disconnectedAt)} ${hour(d.disconnectedAt)}`)
    : d.connected ? 'en cours' : 'inconnue';

/** Durée de la connexion, jusqu'à maintenant si elle dure encore ; '' si la fin est inconnue. */
export const sessionLength = (d: DeviceSession) => {
  const end = d.disconnectedAt ? new Date(d.disconnectedAt).getTime() : d.connected ? Date.now() : NaN;
  return isFinite(end) ? (spoken((end - at(d)) / 1000) || '0 min') : '';
};

/** « lundi 19 octobre 14:50 → 15:42 (52 min) », pour les infobulles et le grand encadré. */
export const sessionSpan = (d: DeviceSession) =>
  `${dayLabel(d.connectedAt)} ${hour(d.connectedAt)} → ${sessionEnd(d)}${sessionLength(d) ? ` (${sessionLength(d)})` : ''}`;

/** Les smartphones comme couche de la carte : un point par connexion, à la date de connexion. */
export const deviceLayer = (list: ChannelDevices[], directs: ChannelDirect[] = []): ExtraLayer => ({
  id: 'devices', label: 'Régies et smartphones', cityLabel: 'Smartphones', color: DEVICE_COLOR,
  legend: [ { label: 'Régies', color: REGIE_COLOR }, { label: 'Smartphones', color: DEVICE_COLOR } ],
  points: [
    ...regiesOf(list.flatMap(c => c.devices.map(d => ({ c, d }))), directs).filter(g => g.geo).map(g => ({
      at: g.start, lat: g.geo!.lat, lon: g.geo!.lon, city: null, place: creatorPlace(g.geo) || 'Lieu inconnu', color: REGIE_COLOR, key: `regie:${g.key}`,
      info: `🖥 Régie · ${g.c.channelName} · ${g.ip} · ${plural(g.rows.length, 'smartphone')}`,
    })),
    ...list.flatMap(c => c.devices.filter(d => d.geo).map(d => ({
      at: at(d), lat: d.geo!.lat, lon: d.geo!.lon, city: d.geo!.city, place: creatorPlace(d.geo) || 'Lieu inconnu', key: deviceKey(c, d),
      info: `📱 ${d.name} · ${c.channelName} · ${d.ip ?? 'IP inconnue'} · ${sessionSpan(d)}`,
    }))),
  ],
});

type Row = { c: ChannelDevices; d: DeviceSession };
/** Clé d'un smartphone, commune au point de la carte et à sa ligne du tableau. */
const deviceKey = (c: ChannelDevices, d: DeviceSession) => `device:${c.channelId}|${d.name}|${d.connectedAt}`;
const PICKED_ROW: React.CSSProperties = { background: 'rgba(244,114,182,0.22)', boxShadow: `inset 4px 0 0 ${DEVICE_COLOR}` };
const ends = (d: DeviceSession) => d.disconnectedAt ? new Date(d.disconnectedAt).getTime() : d.connected ? Infinity : at(d);

/**
 * La diffusion pendant laquelle une connexion a eu lieu : la dernière commencée avant elle, si elle
 * n'était pas finie (à 1 minute près, comme le rattachement d'une régie à sa diffusion plus bas).
 */
const sessionOfRow = (r: Row, sessions: ChannelDirect[]): ChannelDirect | undefined => {
  const t = at(r.d);
  return sessions
    .filter(y => y.channelId === r.c.channelId && t >= new Date(y.startDate).getTime() - 60000
                 && (!y.endDate || t <= new Date(y.endDate).getTime() + 60000))
    .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())[0];
};

/**
 * Les régies : un poste (IP publique) par chaîne ET PAR DIFFUSION, avec les smartphones qui s'y sont
 * connectés pendant celle-ci.
 *
 * Sans le découpage par diffusion, deux directs lancés le même jour depuis le même poste n'en
 * faisaient qu'un : la régie ne se rattachait qu'à la première diffusion, et les smartphones de la
 * seconde s'affichaient sous elle (constaté le 2026-10-29 sur AKINAX, deux directs le 28/10 depuis
 * la même adresse : un seul semblait avoir eu lieu). Une connexion qu'aucune diffusion n'explique
 * garde un groupe par poste, comme avant.
 */
export function regiesOf(rows: Row[], sessions: ChannelDirect[] = []) {
  const m = new Map<string, Row[]>();
  rows.forEach(r => {
    const s = sessionOfRow(r, sessions);
    const k = `${r.c.channelId}|${r.d.regieIp ?? ''}${s ? `|${s.id}` : ''}`;
    m.set(k, [...(m.get(k) ?? []), r]);
  });
  return Array.from(m.entries()).map(([key, rs]) => ({
    key, c: rs[0].c, rows: rs, ip: rs[0].d.regieIp ?? null, geo: rs.find(r => r.d.regieGeo)?.d.regieGeo ?? null,
    client: [...rs].sort((p, q) => at(q.d) - at(p.d)).find(r => r.d.regieClient)?.d.regieClient ?? null,
    start: Math.min(...rs.map(r => at(r.d))), end: Math.max(...rs.map(r => ends(r.d))),
    connected: rs.some(r => r.d.connected), unknownEnd: rs.some(r => !r.d.disconnectedAt && !r.d.connected),
  }));
}

/** « 3 smartphones, dont 1 connecté » pour une carte ou une ligne de chaîne. */
export const devicesText = (devices: DeviceSession[]) => {
  const on = devices.filter(d => d.connected).length;
  return devices.length === 0 ? '' : `${plural(devices.length, 'smartphone')}${on ? `, dont ${on} connecté${on > 1 ? 's' : ''}` : ''}`;
};

// `picked` : le point désigné sur la carte ; sa ligne est surlignée et amenée à l'écran.
// Un clic sur une ligne la désigne (ou la relâche) de la même façon.
const ChannelDevicesPanel: React.FC<{ list: ChannelDevices[]; directs?: ChannelDirect[]; slice: { from: number; to: number } | null; picked: Pick<Picked, 'key' | 'set'> }> = ({ list, directs = [], slice, picked }) => {
  const pickedRow = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => { pickedRow.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }, [picked.key]);
  // Les diffusions dont le détail (programmes, régies, smartphones) est déplié. Repliées par défaut :
  // on retient celles qu'on ouvre, ce qui replie aussi toute diffusion qui apparaît ensuite.
  const [ opened, setOpened ] = useState<ReadonlySet<number>>(() => new Set());

  const rows = list.flatMap(c => c.devices.map(d => ({ c, d })))
                   .filter(({ d }) => slice === null || (at(d) >= slice.from && at(d) < slice.to))
                   .sort((a, b) => Number(b.d.connected) - Number(a.d.connected) || at(b.d) - at(a.d));
  const live = rows.filter(r => r.d.connected).length;

  // Présentation (2026-10-27) : une diffusion par chaîne allumée (« Début diffusion »), et dessous,
  // au même niveau, ce qui passe à l'antenne — la mire ou le nom du programme de la playlist —
  // et la régie quand un direct est à l'antenne. Les smartphones se rangent sous la régie, les
  // invités en retrait sous celui qui était connecté quand ils sont arrivés. Une régie qu'aucune
  // diffusion n'explique (journal sans session) garde l'ancienne présentation, en tête.
  const regies = regiesOf(rows, directs);
  const isGuest = (r: Row) => r.d.byCreator === false;
  const startOf = (x: ChannelDirect) => new Date(x.startDate).getTime();
  const endOf   = (x: ChannelDirect) => x.endDate ? new Date(x.endDate).getTime() : Date.now();
  const sessions = directs.filter(x => slice === null || (startOf(x) >= slice.from && startOf(x) < slice.to))
                          .sort((a, b) => Number(directOpen(b)) - Number(directOpen(a)) || startOf(b) - startOf(a));
  if (rows.length === 0 && sessions.length === 0) return null;

  type Regie = (typeof regies)[number];
  const placed = new Map<number, Regie[]>();
  regies.forEach(g => {
    const x = sessions.filter(y => y.channelId === g.c.channelId && g.start >= startOf(y) - 60000 && (!y.endDate || g.start <= endOf(y) + 60000))
                      .sort((a, b) => startOf(b) - startOf(a))[0];
    if (x) placed.set(x.id, [...(placed.get(x.id) ?? []), g]);
  });
  const orphans = regies.filter(g => !Array.from(placed.values()).some(l => l.includes(g)))
                        .sort((a, b) => Number(b.connected) - Number(a.connected) || b.start - a.start);

  type Line = { diff: ChannelDirect } | { seg: Seg } | { regie: Regie; level: number } | { row: Row; depth: number };
  type Seg  = { name: string | null; start: number; end: number | null };

  // Tous les smartphones d'une régie au même niveau, par heure de connexion : un invité ne
  // s'indente pas sous un autre (il reste signalé « invité » sous son nom).
  const phoneLines = (g: Regie, base: number): Line[] =>
    [...g.rows].sort((p, q) => at(p.d) - at(q.d)).map(r => ({ row: r, depth: base }));

  /** Les programmes de la diffusion, la mire entre eux quand l'intervalle sans trace dépasse 1 minute (et hors des moments où la régie est à l'antenne). */
  const segmentsOf = (x: ChannelDirect, groups: Regie[]): Seg[] => {
    const t0 = startOf(x), tEnd = endOf(x), open = directOpen(x);
    const out: Seg[] = [];
    const mire = (a: number, b: number) => {
      let parts: [number, number][] = [[a, b]];
      groups.forEach(g => { const gs = g.start, ge = g.connected ? Infinity : g.end;
        parts = parts.flatMap(([u, v]) => ge <= u || gs >= v ? [[u, v] as [number, number]]
          : [...(gs > u ? [[u, gs] as [number, number]] : []), ...(ge < v ? [[ge, v] as [number, number]] : [])]); });
      parts.filter(([u, v]) => v - u >= 60000).forEach(([u, v]) => out.push({ name: null, start: u, end: open && v >= tEnd ? null : v }));
    };
    let cursor = t0;
    x.programs.forEach(pr => {
      const ps = new Date(pr.start).getTime(), pe = pr.end ? new Date(pr.end).getTime() : tEnd;
      if (ps > cursor) mire(cursor, ps);
      out.push({ name: pr.name, start: ps, end: open && pe >= tEnd ? null : pe });
      cursor = Math.max(cursor, pe);
    });
    if (cursor < tEnd) mire(cursor, tEnd);
    return out;
  };

  // Ce que contient le détail de chaque diffusion : dit sur sa ligne quand elle est pliée.
  const detail = new Map<number, { phones: number; programs: number }>();
  const ordered: Line[] = [
    ...orphans.flatMap((g): Line[] => [{ regie: g, level: 0 }, ...phoneLines(g, 1)]),
    ...sessions.flatMap((x): Line[] => {
      const groups = placed.get(x.id) ?? [];
      const segs = segmentsOf(x, groups);
      const items: { at: number; lines: Line[] }[] = [
        ...segs.map(sg => ({ at: sg.start, lines: [{ seg: sg }] as Line[] })),
        ...groups.map(g => ({ at: g.start, lines: [{ regie: g, level: 1 } as Line, ...phoneLines(g, 2)] })),
      ].sort((a, b) => a.at - b.at);
      detail.set(x.id, { phones: groups.reduce((n, g) => n + g.rows.length, 0), programs: segs.filter(sg => sg.name !== null).length });
      return [{ diff: x }, ...items.flatMap(it => it.lines)];
    }),
  ];

  // ── Plier / déplier le détail d'une diffusion ──
  // La diffusion qui contient la ligne désignée sur la carte reste dépliée : sans ça, un clic sur
  // un point de la carte désignerait une ligne qu'on ne voit pas.
  let pickedDiff: number | null = null;
  if (picked.key) {
    let current: number | null = null;
    for (const o of ordered) {
      if ('diff' in o) { current = o.diff.id; continue; }
      if (current !== null && (('regie' in o && picked.key === `regie:${o.regie.key}`)
                            || ('row' in o && picked.key === deviceKey(o.row.c, o.row.d)))) pickedDiff = current;
    }
  }
  const isFolded = (id: number) => !opened.has(id) && id !== pickedDiff;
  const toggleFold = (id: number) => {
    if (isFolded(id)) { setOpened(prev => new Set(prev).add(id)); return; }
    // Plier une diffusion qui contient la ligne désignée : on relâche la désignation.
    if (pickedDiff === id) picked.set(null);
    setOpened(prev => { const n = new Set(prev); n.delete(id); return n; });
  };
  let hiding = false;
  const visible = ordered.filter(o => {
    if ('diff' in o) { hiding = isFolded(o.diff.id); return true; }
    return !hiding;
  });
  const regieEnd = (g: (typeof regies)[number]) =>
    g.connected ? <span className={tabs.green}>en cours</span>
    : g.unknownEnd ? <span className={styles.muted}>inconnue</span>
    : `${dayLabel(new Date(g.end).toISOString()) === dayLabel(new Date(g.start).toISOString()) ? '' : `${dayLabel(new Date(g.end).toISOString())} `}${hour(new Date(g.end).toISOString())}`;

  return (
    <div className={tabs.box}>
      <div className={tabs.boxHead}>
        <h3>Direct et smartphones connectés</h3>
        <span className={styles.muted} style={{ fontSize: '0.85rem' }}>
          {plural(sessions.length, 'diffusion')}, {plural(regies.length, 'régie')}, {plural(rows.length, 'connexion')} de smartphone{live ? `, ${plural(live, 'smartphone')} en ce moment` : ''} · position d'après l'adresse IP · sous chaque diffusion : la mire ou le programme, et la régie avec ses smartphones, invités compris au même niveau ; invité = adresse IP différente de la régie (un téléphone du créateur en 4G passe pour invité)
        </span>
        {sessions.length > 0 && <span style={{ fontSize: '0.85rem', display: 'inline-flex', gap: 10 }}>
          <button type="button" className={tabs.linkLike} onClick={() => setOpened(new Set())}>Tout plier</button>
          <button type="button" className={tabs.linkLike} onClick={() => setOpened(new Set(sessions.map(x => x.id)))}>Tout déplier</button>
        </span>}
      </div>
      <div className={styles.tableBox}>
        <table className={`${styles.table} ${tabs.tableWide}`}>
          <thead>
            <tr><th>Chaîne</th><th>Smartphone</th><th>État</th><th>Début</th><th>Fin</th><th>Durée</th><th>Adresse IP</th><th>Ville</th><th>Opérateur</th><th>Mode</th></tr>
          </thead>
          <tbody>
            {visible.map((o, i) => {
              if ('diff' in o) {
                const x = o.diff;
                const open = directOpen(x);
                const len = spoken((endOf(x) - startOf(x)) / 1000) || '0 min';
                const isF = isFolded(x.id);
                const info = detail.get(x.id);
                const summary = isF && info
                  ? [ info.phones > 0 ? plural(info.phones, 'smartphone') : '', info.programs > 0 ? plural(info.programs, 'programme') : '' ].filter(Boolean).join(', ')
                  : '';
                // L'adresse, la ville et l'opérateur de la diffusion sont ceux de sa régie : la première
                // (par heure de connexion) qui a une adresse, quand plusieurs postes y ont participé.
                const regs = [...(placed.get(x.id) ?? [])].sort((a, b) => a.start - b.start);
                const main = regs.find(g => g.ip) ?? regs[0];
                const others = regs.filter(g => g !== main && g.ip && g.ip !== main?.ip).map(g => g.ip);
                return (
                  <tr key={`diff-${x.id}`} className={open ? tabs.rowFresh : undefined} style={{ borderTop: '2px solid rgba(127,127,127,0.35)' }}>
                    <td>
                      <button type="button" className={tabs.linkLike} title="Voir la chaîne dans la liste ci-dessous"
                              onClick={() => { if (!scrollToChannel(x.channelId)) openChannel(x.channelId, 'regie'); }}>{x.channelName}</button>
                      {!x.deleted && <button type="button" className={tabs.linkLike} title="Ouvrir la régie de la chaîne" aria-label={`Ouvrir la régie de ${x.channelName}`}
                              style={{ marginLeft: 6, display: 'inline-flex', verticalAlign: 'middle' }}
                              onClick={() => openChannel(x.channelId, 'regie')}><ExternalLink size={13} /></button>}
                      <div className={styles.muted} style={{ fontSize: '0.78rem' }}>{ownerText(x.owner, x.ownerEmail)}</div>
                    </td>
                    <td className={styles.nowrap}>
                      <button type="button" className={tabs.linkLike} style={{ textDecoration: 'none' }} aria-expanded={!isF}
                              title={isF ? 'Déplier le détail de la diffusion' : 'Plier le détail de la diffusion'}
                              onClick={() => toggleFold(x.id)}>
                        <span aria-hidden="true">{isF ? '▸' : '▾'}</span> Début diffusion
                      </button>
                      {summary && <div className={styles.muted} style={{ fontSize: '0.78rem', paddingLeft: '1rem' }}>{summary}</div>}
                    </td>
                    <td>{open ? <span className={`${tabs.status} ${tabs.statusOk}`}>En direct</span>
                              : <span className={styles.muted}>{x.endDate ? 'Terminée' : 'Fin inconnue'}</span>}</td>
                    <td className={`${styles.nowrap} ${tabs.num}`}>{dayLabel(x.startDate)} {hour(x.startDate)}</td>
                    <td className={`${styles.nowrap} ${tabs.num}`}>{x.endDate ? hour(x.endDate) : open ? <span className={tabs.green}>en cours</span> : <span className={styles.muted}>inconnue</span>}</td>
                    <td className={`${styles.nowrap} ${tabs.num}`}>{x.endDate || open ? len : '—'}</td>
                    <td className={tabs.mono} title={main ? `IP de la régie de cette diffusion${others.length ? ` — autres régies : ${others.join(', ')}` : ''}` : undefined}>{main?.ip ?? '—'}</td>
                    <td>{creatorPlace(main?.geo) || '—'}</td>
                    <td>{main?.geo?.isp ?? '—'}</td>
                    <td>—</td>
                  </tr>
                );
              }
              if ('seg' in o) {
                const sg = o.seg;
                const iso = (t: number) => new Date(t).toISOString();
                return (
                  <tr key={`seg-${i}`}>
                    <td/>
                    <td className={styles.nowrap} style={{ paddingLeft: '1.5rem' }}>
                      <span className={styles.muted}>↳ </span>{sg.name === null ? <span className={styles.muted}>Mire</span> : <>🎞 {sg.name}</>}
                    </td>
                    <td>{sg.end === null ? <span className={`${tabs.status} ${tabs.statusOk}`}>À l'antenne</span> : null}</td>
                    <td className={`${styles.nowrap} ${tabs.num}`}>{hour(iso(sg.start))}</td>
                    <td className={`${styles.nowrap} ${tabs.num}`}>{sg.end === null ? <span className={tabs.green}>en cours</span> : hour(iso(sg.end))}</td>
                    <td className={`${styles.nowrap} ${tabs.num}`}>{spoken(((sg.end ?? Date.now()) - sg.start) / 1000) || '0 min'}</td>
                    <td className={tabs.mono}>—</td><td>—</td><td>—</td><td>—</td>
                  </tr>
                );
              }
              if ('regie' in o) {
                const g = o.regie;
                const sub = o.level > 0;
                return (
                  <tr key={g.key} className={g.connected ? tabs.rowFresh : undefined}
                      ref={picked.key === `regie:${g.key}` ? pickedRow : undefined}
                      onClick={() => picked.set(picked.key === `regie:${g.key}` ? null : `regie:${g.key}`)}
                      style={{ ...(sub ? {} : { borderTop: '2px solid rgba(127,127,127,0.35)' }), cursor: 'pointer', ...(picked.key === `regie:${g.key}` ? PICKED_ROW : {}) }}>
                    {sub ? <td/> : <td>
                      {/* Clic sur le nom : descend jusqu'à la chaîne dans la liste de l'onglet
                          (2026-10-23) ; si elle n'y est pas affichée (filtres, tranche figée),
                          ouvre sa régie comme avant. La régie reste à portée par l'icône. */}
                      <button type="button" className={tabs.linkLike} title="Voir la chaîne dans la liste ci-dessous"
                              onClick={e => { e.stopPropagation(); if (!scrollToChannel(g.c.channelId)) openChannel(g.c.channelId, 'regie'); }}>{g.c.channelName}</button>
                      <button type="button" className={tabs.linkLike} title="Ouvrir la régie de la chaîne" aria-label={`Ouvrir la régie de ${g.c.channelName}`}
                              style={{ marginLeft: 6, display: 'inline-flex', verticalAlign: 'middle' }}
                              onClick={e => { e.stopPropagation(); openChannel(g.c.channelId, 'regie'); }}><ExternalLink size={13} /></button>
                      <div className={styles.muted} style={{ fontSize: '0.78rem' }}>{ownerText(g.c.owner, g.c.ownerEmail)}</div>
                    </td>}
                    <td className={styles.nowrap} style={sub ? { paddingLeft: `${o.level * 1.5}rem` } : undefined}>{sub && <span className={styles.muted}>↳ </span>}<strong>🖥 Régie</strong>
                      <div className={styles.muted} style={{ fontSize: '0.78rem' }} title="Système et navigateur du poste (dernière connexion d'un smartphone)">{g.client ?? 'navigateur inconnu'}</div></td>
                    <td>{g.connected
                          ? <span className={`${tabs.status} ${tabs.statusOk}`}>En direct</span>
                          : <span className={styles.muted}>{g.unknownEnd ? 'Fin inconnue' : 'Terminée'}</span>}</td>
                    <td className={`${styles.nowrap} ${tabs.num}`}>{dayLabel(new Date(g.start).toISOString())} {hour(new Date(g.start).toISOString())}</td>
                    <td className={`${styles.nowrap} ${tabs.num}`} title="Départ du dernier smartphone">{regieEnd(g)}</td>
                    <td className={`${styles.nowrap} ${tabs.num}`}>{!g.connected && !g.unknownEnd ? (spoken((g.end - g.start) / 1000) || '0 min') : '—'}</td>
                    <td className={tabs.mono}>{g.ip ?? '—'}</td>
                    <td>{creatorPlace(g.geo) || '—'}</td>
                    <td>{g.geo?.isp ?? '—'}</td>
                    <td>—</td>
                  </tr>
                );
              }
              const { row: { c, d }, depth } = o;
              const guest = isGuest({ c, d });
              return (
              <tr key={`${c.channelId}-${d.name}-${d.connectedAt}-${i}`} className={d.connected ? tabs.rowFresh : undefined}
                  ref={picked.key === deviceKey(c, d) ? pickedRow : undefined}
                  onClick={() => picked.set(picked.key === deviceKey(c, d) ? null : deviceKey(c, d))}
                  style={{ cursor: 'pointer', ...(picked.key === deviceKey(c, d) ? PICKED_ROW : {}) }}>
                <td/>
                <td className={styles.nowrap} style={{ paddingLeft: `${depth * 1.5}rem` }}>
                  <span className={styles.muted}>↳ </span>📱 {d.name}
                  <div className={styles.muted} style={{ fontSize: '0.78rem', paddingLeft: '1rem' }}>
                    {guest ? 'invité' : d.byCreator ? 'créateur' : 'sans IP'}{d.client ? ` · ${d.client}` : ''}
                  </div>
                </td>                <td>{d.connected
                      ? <span className={`${tabs.status} ${tabs.statusOk}`}>Connecté</span>
                      : <span className={styles.muted}>{d.disconnectedAt ? 'Déconnecté' : 'Fin inconnue'}</span>}</td>
                <td className={`${styles.nowrap} ${tabs.num}`}>{dayLabel(d.connectedAt)} {hour(d.connectedAt)}</td>
                <td className={`${styles.nowrap} ${tabs.num}`}
                    title={!d.disconnectedAt && !d.connected ? 'Aucune déconnexion journalisée (régie fermée avant le téléphone ?)' : undefined}>
                  {d.disconnectedAt ? sessionEnd(d) : d.connected ? <span className={tabs.green}>en cours</span> : <span className={styles.muted}>inconnue</span>}</td>
                <td className={`${styles.nowrap} ${tabs.num}`}>{sessionLength(d) || '—'}</td>
                <td className={tabs.mono} title={d.regieIp ? `IP de la régie : ${d.regieIp}` : undefined}>{d.ip ?? '—'}</td>
                <td>{creatorPlace(d.geo) || '—'}</td>
                <td>{d.geo?.isp ?? '—'}</td>
                <td>{d.transport ?? '—'}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ChannelDevicesPanel;
